/**
 * Drawing Action History and Undo/Redo State Machine.
 *
 * Encapsulates transactional mutations on drawings (add, remove, modify)
 * with predictable state rollback.
 */

export interface DrawingLike {
  id: string;
  type?: string;
  symbol?: string;
  p1?: any;
  p2?: any;
  p3?: any;
  point1?: any;
  point2?: any;
  price?: number;
  [key: string]: any;
}

export type DrawingEvent =
  | { action: "add"; drawing: DrawingLike }
  | { action: "remove"; drawing: DrawingLike; index: number }
  | { action: "modify"; drawingId: string; prev: Partial<DrawingLike>; next?: Partial<DrawingLike> };

export interface UndoResult {
  removedId?: string;
  restoredDrawing?: DrawingLike;
  modifiedId?: string;
  isHline?: boolean;
}

/**
 * Reverts the most recent drawing action in place.
 */
export function applyDrawingUndo(
  drawings: DrawingLike[],
  event: DrawingEvent | undefined,
): UndoResult | null {
  if (!event || !Array.isArray(drawings)) return null;

  if (event.action === "add") {
    const idx = drawings.findIndex(d => d.id === event.drawing.id);
    if (idx >= 0) {
      const d = drawings[idx];
      drawings.splice(idx, 1);
      return {
        removedId: d.id,
        isHline: d.type === "hline",
      };
    }
  } else if (event.action === "remove") {
    const insertIdx = Math.max(0, Math.min(event.index, drawings.length));
    drawings.splice(insertIdx, 0, event.drawing);
    return {
      restoredDrawing: event.drawing,
      isHline: event.drawing.type === "hline",
    };
  } else if (event.action === "modify") {
    const d = drawings.find(x => x.id === event.drawingId);
    if (d) {
      if (event.prev.p1 !== undefined) {
        if (d.p1) d.p1 = event.prev.p1;
        if (d.point1) d.point1 = event.prev.p1;
      }
      if (event.prev.p2 !== undefined) {
        if (d.p2) d.p2 = event.prev.p2;
        if (d.point2) d.point2 = event.prev.p2;
      }
      if (event.prev.p3 !== undefined && d.p3) {
        d.p3 = event.prev.p3;
      }
      if (event.prev.price !== undefined && d.type === "hline") {
        d.price = event.prev.price;
      }
      if (event.prev.anchorTime !== undefined && d.type === "avwap") {
        d.anchorTime = event.prev.anchorTime;
      }
      return {
        modifiedId: d.id,
        isHline: d.type === "hline",
      };
    }
  }

  return null;
}
